import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '@/services/authService';

interface User {
  id: string;
  email: string;
  mfaEnabled: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<{ requiresMFA: boolean; mfaMethod?: string; tempToken?: string }>;
  register: (email: string, password: string, referralCode?: string) => Promise<void>;
  verifyMFA: (token: string, tempToken: string, type: string) => Promise<void>;
  requestOTP: (type: 'EMAIL_OTP' | 'SMS_OTP', destination: string, tempToken: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  handleSessionExpired: () => void;
  setIsAuthenticated: (value: boolean) => void;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSessionExpired, setShowSessionExpired] = useState(false);

  const checkTokenExpiration = (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 > Date.now();
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return false;
    }
  };

  const handleSessionExpired = () => {
    setShowSessionExpired(true);
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  useEffect(() => {
    const initializeAuth = () => {
      console.log('Initializing auth state...');
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      console.log('Retrieved from localStorage - Token exists:', !!token, 'User exists:', !!userStr);
      
      if (token && userStr) {
        try {
          if (!checkTokenExpiration(token)) {
            console.log('Token has expired');
            handleSessionExpired();
            return;
          }
          const parsedUser = JSON.parse(userStr);
          console.log('Setting authenticated state with user:', parsedUser);
          setIsAuthenticated(true);
          setUser(parsedUser);
        } catch (error) {
          console.error('Error parsing user data:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } else {
        console.log('No valid auth data found, setting unauthenticated state');
        setIsAuthenticated(false);
        setUser(null);
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      console.log('Attempting login for email:', email);
      const response = await authService.login(email, password);
      console.log('Login response received:', { requiresMFA: response.requiresMFA, hasToken: !!response.token, hasUser: !!response.user });
      
      if (!response.requiresMFA && response.token && response.user) {
        console.log('Setting auth data after successful login');
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        console.log('Auth data stored in localStorage');
        setIsAuthenticated(true);
        setUser(response.user);
        console.log('Auth state updated - isAuthenticated:', true, 'user:', response.user);
        return { requiresMFA: false };
      }
      
      return {
        requiresMFA: response.requiresMFA || false,
        mfaMethod: response.mfaMethod,
        tempToken: response.tempToken
      };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (email: string, password: string, referralCode?: string) => {
    try {
      await authService.register(email, password, referralCode);
      // Clean up referral code from sessionStorage after successful registration
      if (referralCode) {
        sessionStorage.removeItem('referralCode');
      }
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const verifyMFA = async (token: string, tempToken: string, type: string) => {
    try {
      console.log('Verifying MFA token...');
      const response = await authService.verifyMFA(token, tempToken, type);
      console.log('MFA verification successful, storing new token');
      localStorage.setItem('token', response.token);
      
      try {
        const userData = await authService.fetchUserData(response.token);
        console.log('Successfully fetched user data after MFA');
        setUser(userData as User);
        localStorage.setItem('user', JSON.stringify(userData));
        setIsAuthenticated(true);
      } catch (userError) {
        console.error('Failed to fetch user data after MFA:', userError);
        throw userError;
      }
    } catch (error) {
      console.error('MFA verification error:', error);
      throw error;
    }
  };

  const requestOTP = async (type: 'EMAIL_OTP' | 'SMS_OTP', destination: string, tempToken: string) => {
    try {
      await authService.requestOTP(type, destination, tempToken);
    } catch (error) {
      console.error('OTP request error:', error);
      throw error;
    }
  };

  const logout = async () => {
    console.log('Starting logout process...');
    try {
      const token = localStorage.getItem('token');
      console.log('Retrieved token from localStorage:', !!token);
      
      if (token) {
        console.log('Attempting to logout on server...');
        await authService.logout(token);
        console.log('Server logout successful');
      } else {
        console.log('No token found, skipping server logout');
      }

      console.log('Clearing local storage...');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      console.log('Local storage cleared');

      console.log('Resetting authentication state...');
      setIsAuthenticated(false);
      setUser(null);
      console.log('Authentication state reset complete');
    } catch (error) {
      console.error('Error during logout:', error);
      console.log('Proceeding with local cleanup despite server error...');
      
      console.log('Clearing local storage...');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      console.log('Local storage cleared');

      console.log('Resetting authentication state...');
      setIsAuthenticated(false);
      setUser(null);
      console.log('Authentication state reset complete');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        register,
        verifyMFA,
        requestOTP,
        logout,
        isLoading,
        handleSessionExpired,
        setIsAuthenticated,
        setUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}